import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import callApi from 'client/util/apiCaller';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'Billing.TaxDialog.Title',
    defaultMessage: 'Update tax information',
  },
  businessName: {
    id: 'Billing.TaxDialog.BusinessName',
    defaultMessage: 'Legal business name',
  },
  taxId: {
    id: 'Billing.TaxDialog.TaxId',
    defaultMessage: 'VAT / tax ID',
  },
  taxIdHelp: {
    id: 'Billing.TaxDialog.TaxIdHelp',
    defaultMessage: 'Enter your EU VAT number (e.g. ESB12345678) or UK VAT number.',
  },
  save: {
    id: 'Billing.TaxDialog.Save',
    defaultMessage: 'Save tax information',
  },
  cancel: {
    id: 'Billing.TaxDialog.Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Billing.TaxDialog.Error',
    defaultMessage: 'Could not update tax information. Please check the details and try again.',
  },
  addressRequired: {
    id: 'Billing.TaxDialog.AddressRequired',
    defaultMessage: 'Add a billing address in an EU country or the UK before setting a tax ID.',
  },
});

const emptyForm = {
  business_name: '',
  tax_id: '',
};

function UpdateTaxInfoDialog({ open, onClose, customer, onUpdated }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const billingCountry = customer?.billing_address?.country;

  useEffect(() => {
    if (!open) {
      return;
    }
    const name =
      customer?.business_name ||
      [customer?.first_name, customer?.last_name].filter(Boolean).join(' ');
    setForm({
      business_name: name || '',
      tax_id: customer?.tax_id?.value || '',
    });
    setError(false);
  }, [open, customer]);

  const updateField = field => event => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(false);
    try {
      const ret = await callApi(dispatch, 'users/subscriptions/tax-info', null, 'PUT', {
        business_name: form.business_name,
        tax_id: form.tax_id,
      });
      onUpdated(ret);
      onClose();
    } catch (_err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {!billingCountry && (
          <Typography variant="body2" color="warning.main">
            {formatMessage(messages.addressRequired)}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {formatMessage(messages.error)}
          </Typography>
        )}
        <TextField
          label={formatMessage(messages.businessName)}
          value={form.business_name}
          onChange={updateField('business_name')}
          fullWidth
          required
        />
        <TextField
          label={formatMessage(messages.taxId)}
          value={form.tax_id}
          onChange={updateField('tax_id')}
          fullWidth
          required
          helperText={formatMessage(messages.taxIdHelp)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !billingCountry || !form.business_name.trim() || !form.tax_id.trim()}
        >
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UpdateTaxInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onUpdated: PropTypes.func.isRequired,
};

UpdateTaxInfoDialog.defaultProps = {
  customer: null,
};

export default UpdateTaxInfoDialog;
